import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import { firebaseService } from '../services/firebaseService';
import { NotificationService } from '../services/notificationService';
import { socialService } from '../services/socialService';
import { isFirebaseInitialized } from '@watchlist/shared';
import { useVisibilityInterval } from '../hooks/useVisibilityInterval';
import { Capacitor } from '@capacitor/core';

/** True if the notification row is fully read (handles legacy isRead vs read split). */
function isFirestoreNotificationRead(n: { read?: boolean; isRead?: boolean }): boolean {
  return n.read === true || n.isRead === true;
}

interface Notification {
  id: string;
  recipientId?: string; // For backward compatibility
  userId: string; // New field from Functions backend
  type: 'follow' | 'started_following' | 'collaboration' | 'like_watchlist' | 'share_watchlist' | 'share_movie' | 'movie_added' | 'collaborator_added' | 'movie_watched' | 'user_followed' | 'watchlist_liked' | 'collaboration_invitation' | 'review_liked' | 'access_approved' | 'access_rejected';
  actorId?: string; // For backward compatibility
  actorName?: string; // For backward compatibility
  actorAvatar?: string;
  entityId?: string;
  entityName?: string;
  title: string; // New field from Functions backend
  message: string;
  read: boolean;
  createdAt?: any; // For backward compatibility
  createdAtISO?: string; // ISO string fallback for native (Capacitor drops Timestamp objects)
  timestamp?: any; // New field from Functions backend
  relatedUserId?: string; // New field from Functions backend
  relatedMovieId?: string; // New field from Functions backend
  relatedWatchlistId?: string; // New field from Functions backend
  relatedReviewId?: string; // For review like notifications
  accepted?: boolean; // For collaboration invitations - whether it was accepted
  bundleId?: string;
  isBundled?: boolean;
  bundleCount?: number;
  bundleActors?: string[];
  bundleMessage?: string;
  bundledNotifications?: Notification[];
  metadata?: {
    shareMethod?: 'link' | 'direct' | 'email' | 'social';
    moviePoster?: string;
    movieYear?: string;
    watchlistMovieCount?: number;
    watchlistPosters?: string[];
    movieTitle?: string; // For review like notifications
    likerName?: string; // For review like notifications
    likerAvatar?: string; // For review like notifications
    [key: string]: any;
  };
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  refreshNotifications: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
  user: User | null;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children, user }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to generate titles from messages for legacy notifications
  const generateTitleFromMessage = useCallback((message: string, type: string): string => {
    switch (type) {
      case 'collaboration':
      case 'collaboration_invitation':
        return 'Collaboration Invitation';
      case 'collaborator_added':
        return 'Added as Collaborator';
      case 'like_watchlist':
        return 'Watchlist Liked';
      case 'movie_added':
        return 'Movie Added';
      case 'movie_watched':
        return 'Movie Watched';
      case 'follow':
      case 'started_following':
        return 'New Follower';
      case 'review_liked':
        return 'Review Liked';
      default:
        // Extract a title from the message (take first part before specific keywords)
        const words = message.split(' ');
        if (words.length > 3) {
          return words.slice(0, 3).join(' ') + '...';
        }
        return message.length > 30 ? message.substring(0, 30) + '...' : message;
    }
  }, []);

  // Smart deduplication to remove redundant state-change notifications.
  // When two Firestore rows represent the same logical event, merge read state:
  // the row is "read" only if every duplicate row is read (otherwise the user still has unread work).
  const smartDeduplicateNotifications = useCallback((notifications: Notification[]): Notification[] => {
    const result: Notification[] = [];
    const keyToIndex = new Map<string, number>();

    const stateChangeTypes = ['follow', 'like_watchlist'];

    for (const notification of notifications) {
      if (stateChangeTypes.includes(notification.type)) {
        const key = `${notification.type}_${notification.actorId}_${notification.recipientId || notification.userId}_${notification.entityId || ''}`;

        if (!keyToIndex.has(key)) {
          keyToIndex.set(key, result.length);
          result.push(notification);
        } else {
          const idx = keyToIndex.get(key)!;
          const existing = result[idx];
          const mergedRead = Boolean(existing.read) && Boolean(notification.read);
          result[idx] = { ...existing, read: mergedRead };
          console.log('🧹 Merging duplicate state-change notification:', notification.type, notification.id, '→ read=', mergedRead);
        }
      } else {
        result.push(notification);
      }
    }

    return result;
  }, []);

  const refreshNotifications = useCallback(async () => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Check if Firebase is properly initialized before attempting operations
    // Return early if not - will retry when user state updates
    if (!isFirebaseInitialized()) {
      console.log('NotificationContext: Firebase not yet initialized, will retry when user state updates');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      console.log('🔍 Starting notification fetch for user:', user.uid);

      // OPTIMIZED: Smart fallback pattern - try Functions first, fallback to legacy only on failure
      // This reduces 4 API calls to 2 in the typical case (50% reduction)
      let notifications: any[] = [];
      let usedFallback = false;

      try {
        // Fetch list and unread count separately. On iOS, getUnreadCount (userId + read) can fail
        // (composite index / native plugin) while getUserNotifications still succeeds — Promise.all
        // would drop the whole fetch; decoupling keeps the in-app list working.
        const LIST_TIMEOUT_MS = 20000;
        const listTimeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Notification list fetch timed out after 20s')), LIST_TIMEOUT_MS);
        });

        const functionsNotifications = (await Promise.race([
          NotificationService.getUserNotifications(user.uid, 25),
          listTimeoutPromise
        ])) as any[];

        console.log('✅ Functions API succeeded:', {
          notificationCount: functionsNotifications.length
        });

        // Convert Functions notifications to the expected format
        notifications = functionsNotifications.map(fn => {
          // Map Functions notification types to frontend types
          let mappedType: Notification['type'] = fn.type as Notification['type'];
          if (fn.type === 'user_followed') {
            mappedType = 'follow';
          } else if (fn.type === 'watchlist_liked') {
            mappedType = 'like_watchlist';
          }

          return {
            id: fn.id,
            userId: fn.userId,
            recipientId: fn.userId,
            type: mappedType,
            title: fn.title,
            message: fn.message,
            read: isFirestoreNotificationRead(fn),
            timestamp: fn.timestamp,
            createdAt: fn.timestamp,
            createdAtISO: fn.createdAtISO,
            relatedUserId: fn.relatedUserId,
            relatedMovieId: fn.relatedMovieId,
            relatedWatchlistId: fn.relatedWatchlistId || fn.metadata?.relatedWatchlistId,
            actorId: fn.relatedUserId || fn.actorId || fn.metadata?.actorId,
            actorName: fn.actorName || fn.metadata?.actorName || 'Someone',
            actorAvatar: fn.actorAvatar || fn.metadata?.actorAvatar,
            entityId: fn.entityId || fn.metadata?.entityId,
            entityName: fn.entityName || fn.metadata?.entityName,
            accepted: fn.accepted,
            metadata: fn.metadata
          };
        });

      } catch (functionsError) {
        console.warn('⚠️ Functions API failed, falling back to legacy:', functionsError);
        usedFallback = true;

        // Fallback to legacy system only if Functions fails
        try {
          const LEGACY_LIST_TIMEOUT_MS = 15000;
          const legacyListTimeoutPromise = new Promise<never>((_, reject) => {
            setTimeout(() => reject(new Error('Legacy notification fetch timed out after 15s')), LEGACY_LIST_TIMEOUT_MS);
          });

          const legacyNotifications = (await Promise.race([
            firebaseService.getUserNotifications(user.uid, 25),
            legacyListTimeoutPromise
          ])) as any[];

          const rawUnreadInBatch = legacyNotifications.filter(
            (n: { read?: boolean; isRead?: boolean }) => !isFirestoreNotificationRead(n)
          ).length;

          console.log('✅ Legacy fallback succeeded:', {
            notificationCount: legacyNotifications.length,
            rawUnreadInBatch
          });

          // Convert Legacy notifications to the expected format
          notifications = legacyNotifications.map(ln => ({
            id: ln.id,
            userId: ln.recipientId || ln.userId,
            recipientId: ln.recipientId,
            type: ln.type === 'watchlist_liked' ? 'like_watchlist' : ln.type,
            title: ln.title || generateTitleFromMessage(ln.message, ln.type),
            message: ln.message,
            read: isFirestoreNotificationRead(ln),
            timestamp: ln.timestamp || ln.createdAt,
            createdAt: ln.createdAt,
            createdAtISO: ln.createdAtISO,
            relatedUserId: ln.relatedUserId || ln.actorId,
            relatedMovieId: ln.relatedMovieId,
            relatedWatchlistId: ln.relatedWatchlistId || ln.entityId,
            actorId: ln.actorId,
            actorName: ln.actorName,
            actorAvatar: ln.actorAvatar,
            entityId: ln.entityId,
            entityName: ln.entityName,
            accepted: ln.accepted,
            metadata: ln.metadata
          }));

        } catch (legacyError) {
          // Check if it's a permission error (Firestore security rules not configured)
          const isPermissionError =
            (functionsError instanceof Error && functionsError.message.includes('permission')) ||
            (legacyError instanceof Error && legacyError.message.includes('permission'));

          if (isPermissionError) {
            console.log('🔕 NotificationContext: Notification feature disabled (Firestore permissions not configured)');
          } else {
            console.error('❌ Both notification systems failed:', { functionsError, legacyError });
          }

          // Set empty notifications - notifications are optional
          notifications = [];
        }
      }

      console.log('🔔 Notification fetch complete:', {
        userId: user.uid,
        notificationCount: notifications.length,
        usedFallback
      });

      // Smart deduplication: Remove redundant state-change notifications
      let uniqueNotifications = smartDeduplicateNotifications(notifications);

      // Sort by timestamp/createdAt/createdAtISO
      uniqueNotifications.sort((a, b) => {
        const aTime = a.timestamp?.toDate?.() || a.createdAt?.toDate?.() || new Date(a.timestamp || a.createdAt || a.createdAtISO);
        const bTime = b.timestamp?.toDate?.() || b.createdAt?.toDate?.() || new Date(b.timestamp || b.createdAt || b.createdAtISO);
        return bTime.getTime() - aTime.getTime();
      });

      // Badge count matches what we show (deduped). May be lower than total unread in Firestore if
      // there are more than `limit` recent notifications or unread rows outside the fetched window.
      const visibleUnread = uniqueNotifications.filter(n => !n.read).length;
      setNotifications(uniqueNotifications);
      setUnreadCount(visibleUnread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  }, [user, generateTitleFromMessage, smartDeduplicateNotifications]);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user) return;

    try {
      // Try both systems - one will succeed, the other will fail silently
      await Promise.allSettled([
        NotificationService.markAsRead(notificationId),
        firebaseService.markNotificationAsRead(notificationId)
      ]);

      // Update local state
      setNotifications(prev =>
        prev.map(notification =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        )
      );

      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, [user]);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;

    try {
      // Try both systems
      await Promise.allSettled([
        NotificationService.markAllAsRead(user.uid),
        firebaseService.markAllNotificationsAsRead(user.uid)
      ]);

      // Update local state
      setNotifications(prev =>
        prev.map(notification => ({ ...notification, read: true }))
      );

      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [user]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!user) return;

    try {
      // Try both systems
      await Promise.allSettled([
        NotificationService.deleteNotification(notificationId),
        firebaseService.deleteNotification(notificationId)
      ]);

      // Update local state
      setNotifications(prev =>
        prev.filter(notification => notification.id !== notificationId)
      );

      // Update unread count if it was unread
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  }, [user, notifications]);

  // Load notifications when user changes
  useEffect(() => {
    refreshNotifications();
  }, [refreshNotifications]);

  // Native (esp. iOS): WKWebView does not always mirror desktop visibility; refresh when app returns to foreground.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const onResume = () => {
      if (user) void refreshNotifications();
    };
    window.addEventListener('watchlistsAppResumed', onResume);
    return () => window.removeEventListener('watchlistsAppResumed', onResume);
  }, [user, refreshNotifications]);

  // Poll for new notifications, only when app is visible.
  // Native uses 2 minutes (foreground refreshes are handled by watchlistsAppResumed above).
  // Web uses 60 seconds since it has real-time onSnapshot for most paths.
  const notificationPollInterval = Capacitor.isNativePlatform() ? 120000 : 60000;
  useVisibilityInterval(() => {
    if (user) {
      refreshNotifications();
    }
  }, notificationPollInterval);

  const contextValue: NotificationContextType = {
    notifications,
    unreadCount,
    isLoading,
    error,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider; 